<template>
  <v-layout
    v-resize="onResize"
    class="pl-4"
    :style="{ height: contentHeight + 'px' }"
    style="overflow-y: scroll"
    column
    @mouseover="highlightArea"
    @mouseout="removeHighlightedArea"
  >
    <div v-show="!analysisRunning">
      <v-form>
        <template v-if="innspillErFerdig">
          <template v-if="analysisFailed"
            ><h2 class="mt-2">Det oppstod en feil</h2>
            <p>
              Vennligst prøv på ny. Kontakt systemansvarlig dersom feilen
              vedvarer.
            </p>
            <v-btn class="ml-0" color="primary" @click="resetAnalysis"
              >Prøv igjen</v-btn
            ></template
          >
          <template v-else
            ><h2 class="mt-2">Innspillet er klart</h2>
            <p>
              Dersom nedlasting ikke starter automatisk kan du klikke knappen
              under for å starte nedlastingen av dokumentet i nettleseren.
            </p>
            <v-btn class="ml-0" color="primary" @click="lastNedInnspill"
              >Last ned innspill</v-btn
            ></template
          >
        </template>
        <template v-else>
          <h2 class="mt-2">Generer innspill til arealplan (beta versjon)</h2>
          <p>
            Gi planen en ID og et navn ved å fylle ut feltene under og klikk
            deretter knappen "Start generering" for å generere innspill til
            arealplan i Word format.
          </p>
          <v-text-field
            class="analysis-text-field"
            required
            label="Plannavn"
            hint="Navn (beskrivelse) av planen"
            aria-label="Plannavn"
            box
            v-model="plannavnValue"
          ></v-text-field>
          <v-text-field
            class="analysis-text-field"
            required
            label="Plan ID"
            hint="Unik id til planforslaget"
            aria-label="Plan ID"
            box
            v-model="planIdValue"
          ></v-text-field>
          <v-btn
            class="ml-0"
            color="primary"
            :disabled="!plannavnValue || !planIdValue"
            @click="genererInnspill"
            >Start generering av innspill</v-btn
          >
        </template>
      </v-form>
    </div>
    <v-layout align-center justify-center column v-show="analysisRunning">
      <v-progress-circular indeterminate color="red"></v-progress-circular>
      <p class="body-1 mt-2">{{ loadingBeskrivelse }}</p>
    </v-layout>
  </v-layout>
</template>

<script>
import { loadModules } from "esri-loader";
import MappingMixin from "@/mixins/mappingMixin";
import { mapState } from "vuex";
import axios from "axios";

export default {
  name: "AnalysisArealplanInnspill",
  mixins: [MappingMixin],
  computed: {
    ...mapState(["is3d", "bottomTabsHeight"]),
    contentHeight() {
      return (this.windowSize.y / 100) * this.bottomTabsHeight - 60;
    }
  },
  data: function() {
    return {
      loading: false,
      plannavnValue: null,
      planIdValue: null,
      highlightGraphic: null,
      analysisRunning: false,
      loadingBeskrivelse: "",
      innspillErFerdig: false,
      analysisFailed: false,
      windowSize: {
        x: 0,
        y: 0
      }
    };
  },
  props: {
    polygonGeometry: Object
  },
  methods: {
    async genererInnspill() {
      this.analysisRunning = true;
      this.loadingBeskrivelse = "Kjører innspillsanalyse for valgt polygon";
      loadModules(["esri/tasks/Geoprocessor"]).then(([Geoprocessor]) => {
        let geoprocessor = new Geoprocessor(
          process.env.VUE_APP_ANALYSIS_AREALPLAN_GEOPROCESSING_SERVICE_URL
        );
        geoprocessor
          .submitJob({
            _geom: JSON.stringify(this.polygonGeometry.toJSON()),
            planId: this.planIdValue,
            planNavn: this.plannavnValue
          })
          .then(jobInfo => {
            let jobId = jobInfo.jobId;
            geoprocessor
              .waitForJobCompletion(jobId)
              .then(() => {
                geoprocessor.getResultData(jobId, "jres").then(result => {
                  this.lastNedWordInnspill(result.value.RpId);
                });
              })
              .catch(error => {
                console.error(error);
                this.analysisRunning = false;
              });
          })
          .catch(error => {
            if (error.jobStatus == "esriJobFailed") {
              this.analysisRunning = false;
              this.analysisFailed = true;
              this.innspillErFerdig = true;
            }
          });
      });
    },
    resetAnalysis() {
      this.analysisRunning = false;
      this.innspillErFerdig = false;
      this.analysisFailed = false;
    },
    lastNedInnspill() {
      const href = URL.createObjectURL(this.analysisResponse);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute(
        "download",
        this.planIdValue + "_" + this.plannavnValue + ".docx"
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    },
    lastNedWordInnspill(planId) {
      this.analysisFailed = false;
      this.loadingBeskrivelse = "Genererer Word dokument";
      const url = process.env.VUE_APP_AREALPLAN_ANALYSIS_SERVICE_URL + planId;
      axios({
        url: url,
        method: "GET",
        responseType: "blob"
      })
        .then(response => {
          this.analysisResponse = response.data;
          this.lastNedInnspill();
          this.analysisRunning = false;
        })
        .catch(error => {
          console.error(error);
          this.analysisFailed = true;
        })
        .finally(() => {
          this.loadingBeskrivelse = "";
          this.analysisRunning = false;
          this.innspillErFerdig = true;
        });
    },
    onResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight };
    },
    highlightArea: function() {
      let view = this.is3d ? this.$theSceneViewObject : this.$theMapViewObject;
      view.graphics.add(this.highlightGraphic);
    },
    removeHighlightedArea: function() {
      let view = this.is3d ? this.$theSceneViewObject : this.$theMapViewObject;
      view.graphics.remove(this.highlightGraphic);
    },
    createHighlightGraphic: function() {
      loadModules(["esri/Graphic"]).then(([Graphic]) => {
        let view = this.is3d
          ? this.$theSceneViewObject
          : this.$theMapViewObject;
        this.highlightGraphic = new Graphic({
          geometry: this.polygonGeometry,
          symbol: this.getDefaultHighlightSymbol(view, "polygon")
        });
      });
    }
  },
  mounted() {
    this.onResize();
    this.createHighlightGraphic();
    loadModules(["esri/core/urlUtils"]).then(([urlUtils]) => {
      urlUtils.addProxyRule({
        urlPrefix:
          process.env.VUE_APP_ANALYSIS_AREALPLAN_GEOPROCESSING_SERVICE_URL,
        proxyUrl: process.env.VUE_APP_INTERNAL_PROXY_URL
      });
    });
  }
};
</script>

<style scoped>
.analysis-text-field {
  max-width: 400px;
}
</style>
